/* import __COLOCATED_TEMPLATE__ from './change-ticket-type-panel.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { TicketTypeResource } from 'embercom/components/inbox2/ticket-type-resource';
import { useResource } from 'ember-resources';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type Snackbar from 'embercom/services/snackbar';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxApi from 'embercom/services/inbox-api';
import type TicketAttributeSummary from 'embercom/objects/inbox/ticket-attribute-summary';
import type InboxState from 'embercom/services/inbox-state';
import type TicketStateService from 'embercom/services/ticket-state-service';
import { action } from '@ember/object';
import type LinkedTicketsResource from './linked-tickets-resource';

export interface ChangeTicketTypePanelArgs {
  originalConversation: Conversation;
  newTicketTypeId?: number;
  hidePanel: () => void;
  linkedTicketsResource: LinkedTicketsResource;
}

interface Signature {
  Args: ChangeTicketTypePanelArgs;
}

export default class ChangeTicketTypePanel extends Component<Signature> {
  @service declare snackbar: Snackbar;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare ticketStateService: TicketStateService;

  @tracked selectedTicketCustomStateId: number | undefined;

  readonly ticketTypeResource = useResource(this, TicketTypeResource, () => ({
    ticketTypeId: this.args.newTicketTypeId,
    conversation: this.args.originalConversation,
    matchAttributesByNameAndType: true,
  }));

  get shouldShowAttributes() {
    let hasSelectedType = !!this.ticketTypeResource.selectedTicketType;
    let hasAttributes = this.ticketTypeResource.ticketHasAttributes;
    let isLoading = this.ticketTypeResource.isLoading;
    return hasSelectedType && hasAttributes && !isLoading;
  }

  get preselectedStatusId(): number | undefined {
    let type = this.ticketTypeResource.selectedTicketType;
    let conversation = this.args.originalConversation;
    let customStateId = conversation.ticketCustomStateId;
    if (!type || customStateId == null) {
      return undefined;
    }
    return this.ticketStateService.ticketStateBelongsToTicketType(type, customStateId)
      ? customStateId
      : undefined;
  }

  get effectiveSelectedStatusId(): number | undefined {
    return this.selectedTicketCustomStateId ?? this.preselectedStatusId;
  }

  get statusRequiredAndEmpty(): boolean {
    return this.effectiveSelectedStatusId == null;
  }

  get statusSelectHasError(): boolean {
    return this.statusRequiredAndEmpty && (taskFor(this.changeTicketType).performCount ?? 0) > 0;
  }

  @action
  onStatusSelect(customStateId: number) {
    this.selectedTicketCustomStateId = customStateId;
  }

  @task({ drop: true })
  *changeTicketType(): any {
    if (this.args.newTicketTypeId === undefined) {
      return;
    }

    if (this.statusRequiredAndEmpty) {
      this.snackbar.notifyError(
        this.intl.t('inbox.change-ticket-type-panel.errors.status-required'),
      );
      return;
    }

    if (this.ticketTypeResource.missingRequiredAttributes.length > 0) {
      let errorMessage = this.intl.t('inbox.new-conversation.errors.invalid-ticket-attributes', {
        required_attributes: this.ticketTypeResource.missingRequiredAttributes
          .map((attr) => attr.descriptor.name)
          .join(', '),
      });
      this.snackbar.notifyError(errorMessage);
      return;
    }

    if (this.ticketTypeResource.attributesWithInvalidTypes.length > 0) {
      let errorMessage = this.intl.t(
        'inbox.new-conversation.errors.invalid-ticket-attributes-types',
        {
          invalid_attributes: this.ticketTypeResource.attributesWithInvalidTypes
            .map((attr) => attr.descriptor.name)
            .join(', '),
        },
      );
      this.snackbar.notifyError(errorMessage);
      return;
    }

    let ticketAttributes = this.ticketTypeResource.ticketAttributes?.map(
      (attributeSummary: TicketAttributeSummary) => {
        return {
          descriptor_id: attributeSummary.descriptor.id,
          value: attributeSummary.serializedValue,
        };
      },
    );

    // TODO: Implement backend endpoint for changing ticket type
    // For now, just log what would be sent to the API and close panel
    // eslint-disable-next-line no-console
    console.log('Change ticket type request:', {
      conversationId: this.args.originalConversation.id,
      ticketTypeId: this.args.newTicketTypeId,
      ticket_custom_state_id: this.effectiveSelectedStatusId,
      ticketAttributes,
    });

    // Placeholder yield - will be replaced with actual API call
    yield Promise.resolve();

    this.args.hidePanel();
  }

  get isChangingTicketType() {
    return taskFor(this.changeTicketType).isRunning;
  }

  @action
  noopCompanySelection(_companyId: string | null, _isDefaultSelection: boolean) {
    // No-op: Company selection is disabled in read-only mode
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ChangeTicketTypePanel': typeof ChangeTicketTypePanel;
    'inbox2/change-ticket-type-panel': typeof ChangeTicketTypePanel;
  }
}
